import { enhancedApi } from './generated/bulkAgentThreadsService';

// ---------------------------------------------------------------------------
// Notion push types
// ---------------------------------------------------------------------------

export interface NotionPushPreview {
  required_columns_present: boolean;
  missing_required_columns: string[];
  dynamic_columns_detected: string[];
  will_create_dynamic_columns_in_notion: string[];
  cases_sample: Array<{
    matter: string;
    status: string;
    commentary: string;
    dynamic_fields: Record<string, string>;
  }>;
}

interface PushToNotionRequest {
  operationId: string;
  targetPageId: string;
  dryRun: boolean;
}

export interface PushStartResponse {
  workflow_id: string;
  message: string;
}

export interface PushStatusResponse {
  status: string;
  error?: string | null;
}

/** Type guard to discriminate a dry-run preview from an actual push response. */
export const isNotionPushPreview = (
  resp: PushStartResponse | NotionPushPreview,
): resp is NotionPushPreview => 'required_columns_present' in resp;

// ---------------------------------------------------------------------------
// Filter options types
// ---------------------------------------------------------------------------

export interface BulkOperationFilterOptionsResponse {
  creators: { id: string; name: string }[];
  firms: { id: string; name: string }[];
}

// ---------------------------------------------------------------------------
// Enhanced API with Notion push & filter options endpoints
// ---------------------------------------------------------------------------

export const bulkAgentThreadsEnhancedApi = enhancedApi.injectEndpoints({
  endpoints: (build) => ({
    pushToNotion: build.mutation<PushStartResponse | NotionPushPreview, PushToNotionRequest>({
      query: ({ operationId, targetPageId, dryRun }) => ({
        url: `/api/agent-threads/bulk/${operationId}/push-to-notion/`,
        method: 'POST',
        body: { target_page_id: targetPageId, dry_run: dryRun },
      }),
    }),

    getNotionPushStatus: build.query<PushStatusResponse, { operationId: string; workflowId: string }>({
      query: ({ operationId, workflowId }) => ({
        url: `/api/agent-threads/bulk/${operationId}/push-status/`,
        params: { workflow_id: workflowId },
      }),
    }),

    bulkAgentOperationFilterOptions: build.query<BulkOperationFilterOptionsResponse, void>({
      query: () => ({ url: '/api/agent-threads/bulk/filter-options/' }),
    }),
  }),
  overrideExisting: false,
});

export const {
  usePushToNotionMutation,
  useGetNotionPushStatusQuery,
  useBulkAgentOperationFilterOptionsQuery,
} = bulkAgentThreadsEnhancedApi;
